import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Modal from "aldoo-ra/Modal"
import DataTable from "aldoo-ra/DataTable"
import Badge from "aldoo-ra/Badge"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { api } from "aldoo-ra/API"
import { getPlatformIcon } from "aldoo-ra/Admin/ProductLicensing/PlatformIcons"
import { DevicePhoneMobileIcon } from "@heroicons/react/24/outline"
import moment from "moment"

/**
 * Modal for viewing and managing user devices
 */
const DevicesModal = ({ user, isOpen, onClose }) => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [platformFilter, setPlatformFilter] = useState([{ value: "all", label: "All Platforms" }])

  // Initialize CRUD API
  const crudAPI = api("crud")

  // Refresh the table when dialog opens
  useEffect(() => {
    if (!isOpen) return
    setRefreshKey((prev) => prev + 1)
  }, [isOpen])

  // Refresh when platform filter changes
  useEffect(() => {
    setRefreshKey((prev) => prev + 1)
  }, [platformFilter])

  if (!user) return null

  // API method to get devices for this user
  const getUserDevices = async (requestFields = {}) => {
    try {
      // Get the device IDs from the user object and deduplicate
      const allDeviceIds = user.devices || []
      const deviceIds = [...new Set(allDeviceIds)] // Remove duplicates

      if (deviceIds.length === 0) {
        return {
          result: true,
          data: [],
          totalItems: 0
        }
      }

      // Build platform filter
      const platformMatch = {}
      if (platformFilter.length > 0 && !platformFilter.some(f => f.value === "all")) {
        platformMatch.platform = { $in: platformFilter.map(f => f.value) }
      }

      // Query devices using the CRUD API - try multiple ID fields
      const fields = {
        ...requestFields,
        model: "UserDevice",
        match: {
          $or: [
            { _id: { $in: deviceIds } },
            { deviceId: { $in: deviceIds } },
            { deviceID: { $in: deviceIds } }
          ],
          ...platformMatch,
          ...requestFields.match
        },
        sort: { created: -1 },
      }

      return await crudAPI({ get: fields })
    } catch (error) {
      console.error("Failed to fetch user devices:", error)
      return {
        result: false,
        data: [],
        totalItems: 0
      }
    }
  }

  const formatDate = (date) => {
    if (!date) return "Never"
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  const getTimeAgo = (date) => {
    if (!date) return "Never"
    try {
      return moment(date).fromNow()
    } catch (error) {
      return "Invalid date"
    }
  }

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown Model"

    // Check if it's a user agent string (contains Mozilla)
    if (!userAgent.includes("Mozilla")) {
      return userAgent // Return as-is if it's not a user agent
    }

    try {
      let browser = "Unknown Browser"
      let os = "Unknown OS"

      // Extract browser
      if (userAgent.includes("Chrome")) {
        browser = "Chrome"
      } else if (userAgent.includes("Firefox")) {
        browser = "Firefox"
      } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
        browser = "Safari"
      } else if (userAgent.includes("Edge")) {
        browser = "Edge"
      } else if (userAgent.includes("Mozilla")) {
        browser = "Mozilla"
      }

      // Extract OS
      if (userAgent.includes("Macintosh") || userAgent.includes("Mac OS X")) {
        os = "macOS"
      } else if (userAgent.includes("Windows")) {
        os = "Windows"
      } else if (userAgent.includes("Linux")) {
        os = "Linux"
      } else if (userAgent.includes("Android")) {
        os = "Android"
      } else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
        os = "iOS"
      }

      return `${browser} • ${os}`
    } catch (error) {
      return userAgent
    }
  }

  const getStatusBadge = (device) => {
    if (!device.enabled) return <Badge variant="admin_error">Disabled</Badge>
    if (device.online) return <Badge variant="admin_success">Online</Badge>
    if (device.login) return <Badge variant="admin_info">Logged In</Badge>
    return <Badge variant="admin_warning">Offline</Badge>
  }


  // Define columns for the devices table
  const tableItemRender = [
    {
      id: "device",
      title: "Device",
      headerClassName: "w-[300px] xl:w-1/3",
      render: (_, device) => (
        <div className="flex items-center space-x-3">
          {getPlatformIcon(device.platform, "w-8 h-8 text-admin_text_secondary")}

          <div className="w-[200px] xl:w-[360px]">
            <Typography
              variant="admin_label"
              className="truncate my-0"
            >
              {device.name || device.deviceName || "Unknown Device"}
            </Typography>

            <div className="w-full flex items-center gap-2 flex-nowrap whitespace-nowrap">
              <Tooltip display="block" content={device.model || "Unknown Model"}>
                <Typography
                  variant="admin_txt1_dim"
                  className="whitespace-nowrap"
                >
                  {parseUserAgent(device.model)}
                </Typography>
              </Tooltip>

              <span className="text-admin_text_secondary">•</span>

              <Typography
                variant="admin_txt1_dim"
                className="whitespace-nowrap"
              >
                {device.platform || "Unknown"}
              </Typography>

              {device.deviceID && (
                <>
                  <span className="text-admin_text_secondary">•</span>

                  <Tooltip display="block" content={
                    <div className="text-center">
                      <Typography
                        variant="admin_txt1"
                        className="block"
                      >
                        {device.deviceID}
                      </Typography>

                      <Typography variant="admin_txt2_dim">
                        Click to copy
                      </Typography>
                    </div>
                  }>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(device.deviceID)
                          showNotification({
                            message: "Copied to clipboard",
                            className: "bg-admin_primary text-admin_text_inv"
                          })
                        } catch (error) {
                          console.error("Failed to copy:", error)
                        }
                      }}
                      className="max-w-[70px] xl:max-w-[170px] font-mono truncate whitespace-nowrap text-admin_text_secondary hover:text-admin_primary transition-colors text-left"
                    >
                      {device.deviceID}
                    </button>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[80px]",
      render: (_, device) => getStatusBadge(device),
    },
    {
      id: "ip",
      title: "IP",
      headerClassName: "w-1/5",
      render: (_, device) => (
        device.ip ? (
          <Tooltip display="block" content={
            <div className="text-center">
              <Typography
                variant="admin_txt1"
                className="block"
              >
                {device.ip}
              </Typography>

              <Typography variant="admin_txt2_dim">
                Click to copy
              </Typography>
            </div>
          }>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(device.ip)
                  showNotification({
                    message: "Copied to clipboard",
                    className: "bg-admin_primary text-admin_text_inv"
                  })
                } catch (error) {
                  console.error("Failed to copy:", error)
                }
              }}
              className="w-full font-mono truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
            >
              {device.ip}
            </button>
          </Tooltip>
        ) : (
          <Typography
            variant="admin_txt1"
            className="text-admin_text font-mono"
          >
            N/A
          </Typography>
        )
      ),
    },
    {
      id: "lastActivity",
      title: "Last Activity",
      headerClassName: "w-[200px]",
      render: (_, device) => (
        <div className="text-sm">
          <div className="flex gap-1 flex-nowrap mb-1">
            <span className="text-admin_text_secondary">
              Login:
            </span>

            <Tooltip content={getTimeAgo(device.lastLogin)}>
              <span className="text-admin_text whitespace-nowrap cursor-help">
                {formatDate(device.lastLogin)}
              </span>
            </Tooltip>
          </div>

          <div className="flex gap-1 flex-nowrap">
            <span className="text-admin_text_secondary">
              Ping:
            </span>

            <Tooltip content={getTimeAgo(device.lastPing)}>
              <span className="text-admin_text cursor-help">
                {formatDate(device.lastPing)}
              </span>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      id: "created",
      title: "Created",
      headerClassName: "w-[170px]",
      render: (_, device) => (
        <Tooltip content={getTimeAgo(device.created)}>
          <Typography
            variant="admin_txt1_dim"
            className="whitespace-nowrap cursor-help"
          >
            {formatDate(device.created)}
          </Typography>
        </Tooltip>
      ),
    },
  ]

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      className="md:absolute w-full md:w-[95%] h-full md:h-[100vh] top-[15px]"
      zIndex={200}
    >
      <div className="p-4">
        <Typography
          component="h1"
          variant="admin_h3"
          className="mb-4"
        >
          Devices - {user.name}
        </Typography>

        {/* Platform filter */}
        <div className="mb-4">
          <div className="flex items-end gap-4">
            <div className="flex flex-col">
              <Typography
                variant="admin_txt2_dim"
                className="mb-0.5"
              >
                Platform
              </Typography>

              <DropdownSelect
                variant="admin"
                options={[
                  { value: "all", label: "All Platforms" },
                  { value: "iOS", label: "iOS" },
                  { value: "Android", label: "Android" },
                  { value: "Windows", label: "Windows" },
                  { value: "Mac", label: "Mac" },
                  { value: "Linux", label: "Linux" },
                  { value: "Web", label: "Web" },
                ]}
                value={platformFilter}
                onChange={(values) => setPlatformFilter(values?.length ? values : [{ value: "all", label: "All Platforms" }])}
                fixedWidth="150px"
                showCheckbox={false}
                searchEnabled={false}
              />
            </div>
          </div>
        </div>

        {/* Data table for displaying devices */}
        <div className="h-[70vh]">
          <DataTable
            variant="admin"
            refreshKey={refreshKey}
            apiMethod={getUserDevices}
            preferredHeight="70vh"
            itemHeight="h-[70px]"
            localUserPreferencesKey="admin.platformAccess.users"
        styleOverrides={{
              table: "w-full table-fixed",
              searchInput: "absolute top-[45px] right-[40px] w-full max-w-[300px] xl:max-w-[360px] ml-6 xl:ml-8"
            }}
            searchField="name platform model deviceID ip"
            searchObject={{}}
            sort={{ created: -1 }}
            renders={tableItemRender}
            noData={
              <div className="min-h-[400px] flex items-center justify-center">
                <div className="text-center">
                  <DevicePhoneMobileIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-3" />

                  <Typography variant="admin_body_dim">
                    No devices connected
                  </Typography>
                </div>
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}

export default DevicesModal
